// An SVG with only a `viewBox` (no width/height) has no intrinsic size, so <img>+canvas
// rasterises it blank. Inject width/height from the viewBox so it draws. Non-SVG files pass
// through untouched.
const withSvgSize = async (file) => {
  if (file.type !== "image/svg+xml") {
    return file;
  }
  const text = await file.text();
  if (/<svg[^>]*\b(width|height)\s*=/i.test(text)) {
    return file;
  }
  const viewBox = text.match(
    /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/i,
  );
  const width = viewBox ? viewBox[1] : 300;
  const height = viewBox ? viewBox[2] : 300;
  const patched = text.replace(
    /<svg\b/i,
    `<svg width="${width}" height="${height}"`,
  );
  return new File([patched], file.name, { type: "image/svg+xml" });
};

// Yüklenen dosyayı <img> olarak çözer. SVG'de viewBox yaması burada devreye giriyor.
const loadImage = async (file) => {
  const source = await withSvgSize(file);
  const url = URL.createObjectURL(source);

  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("image load failed"));
      img.src = url;
    });
  } finally {
    // Çözülen <img> veriyi kendi tuttuğu için URL'i hemen bırakmak güvenli; hata yolunda da
    // bırakılmalı, yoksa her başarısız yükleme bir blob sızdırır.
    URL.revokeObjectURL(url);
  }
};

const canvasToFile = (canvas, name, type, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(new File([blob], name, { type }))
          : reject(new Error("toBlob failed")),
      type,
      quality,
    ),
  );

// Zemin temizliğinin ayarları. Zemin rengi SABİT DEĞİL, çizilen alanın dört köşesinden
// örnekleniyor: sabit "beyaza yakın" eşiği krem/kırık beyaz zeminlerde ıskalıyordu — #f8f5ee'nin en
// düşük kanalı 238, eşiğin bir tık altında kalıp temizlik yerine %5'lik bir hayalet bırakıyordu.
const BG_TOLERANCE = 30;
// Toleransın ötesindeki yumuşama bandı. Sert kesim, yazı ve eğri kenarlarında zemin renginden bir
// hale bırakıyor.
const BG_SOFT = 26;
// Bir aday silindikten sonra bu kadar içerik kalmıyorsa o aday zemin değil, logonun kendisidir.
// Sıradaki adaya geçmek için kullanılıyor (bkz. clearBackground).
const MIN_CONTENT_RATIO = 0.03;

const channelDistance = (pixels, index, r, g, b) =>
  Math.abs(pixels[index] - r) +
  Math.abs(pixels[index + 1] - g) +
  Math.abs(pixels[index + 2] - b);

// Bir pikselin zemin rengine uzaklığına göre alacağı alfa. Sayma ve yazma geçişleri aynı kuralı
// kullansın diye burada; iki kopya olduğunda yumuşama formülünü değiştirmek ikisini de gezmek
// demekti.
const alphaFor = (pixels, index, r, g, b) => {
  const distance = channelDistance(pixels, index, r, g, b);

  if (distance <= BG_TOLERANCE) {
    return 0;
  }

  if (distance < BG_TOLERANCE + BG_SOFT) {
    return Math.round(
      (pixels[index + 3] * (distance - BG_TOLERANCE)) / BG_SOFT,
    );
  }

  return pixels[index + 3];
};

// Gerçekten piksel bulunan alanın sınır kutusu. Tuvalin köşeleri güvenilmez: kenar boşluğu
// verildiğinde ya da kullanıcı uzaklaşıp kadrajı kaynağın dışına taşırdığında (kare çerçeveye geniş
// bir logoyu sığdırmanın tek yolu bu) köşeler şeffaf kalıyor ve her kaynak "zemini yok" sayılıyordu.
const opaqueBounds = (pixels, width, height) => {
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0, i = 3; y < height; y += 1) {
    for (let x = 0; x < width; x += 1, i += 4) {
      if (pixels[i] !== 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  return maxX < minX ? null : { minX, minY, maxX, maxY };
};

// Zemin adayları: sınır kutusunun ÇEVRESİ boyunca örnekleyip renkleri kümeliyoruz, sık görülen
// başta.
//
// Yalnızca dört köşeye bakan sürüm yakınlaştırmada bozuluyordu: kadraj amblemin içine girdiğinde
// dört köşe de logonun kendi siyahına düşüyor, "hepsi aynı" testini geçiyor ve zemin SİYAH
// sanılıyordu. Sonra siyahı silmek neredeyse her şeyi sileceği için içerik eşiği devreye girip
// işlemi iptal ediyordu — kullanıcı anahtarı açık görüp hiçbir şeyin değişmediğine bakıyordu.
//
// Kenardan biraz içeriden örnekleniyor ve yalnızca tam opak pikseller sayılıyor: kadraj kaynağın
// dışına taştığında sınır pikselleri yarı saydam bir saçak oluyor ve renkleri güvenilmez.
const BORDER_STEPS = 8;
const BORDER_MAX_CANDIDATES = 3;

const backgroundCandidates = (pixels, width, bounds) => {
  const { minX, minY, maxX, maxY } = bounds;
  const boxWidth = maxX - minX;
  const boxHeight = maxY - minY;

  if (boxWidth < 4 || boxHeight < 4) {
    return [];
  }

  const left = minX + Math.max(1, Math.round(boxWidth * 0.02));
  const right = maxX - Math.max(1, Math.round(boxWidth * 0.02));
  const top = minY + Math.max(1, Math.round(boxHeight * 0.02));
  const bottom = maxY - Math.max(1, Math.round(boxHeight * 0.02));
  const clusters = [];

  const add = (x, y) => {
    const index = (y * width + x) * 4;

    if (pixels[index + 3] < 250) {
      return;
    }

    const found = clusters.find(
      (cluster) =>
        channelDistance(pixels, index, ...cluster.rgb) <= BG_TOLERANCE,
    );

    if (found) {
      found.count += 1;

      return;
    }

    clusters.push({
      rgb: [pixels[index], pixels[index + 1], pixels[index + 2]],
      count: 1,
    });
  };

  for (let step = 0; step <= BORDER_STEPS; step += 1) {
    const ratio = step / BORDER_STEPS;
    const x = Math.round(left + ratio * (right - left));
    const y = Math.round(top + ratio * (bottom - top));

    add(x, top);
    add(x, bottom);
    add(left, y);
    add(right, y);
  }

  return clusters
    .sort((a, b) => b.count - a.count)
    .slice(0, BORDER_MAX_CANDIDATES)
    .map((cluster) => cluster.rgb);
};

const clearBackground = (ctx, width, height) => {
  const image = ctx.getImageData(0, 0, width, height);
  const pixels = image.data;
  const bounds = opaqueBounds(pixels, width, height);

  if (!bounds) {
    return;
  }

  const { minX, minY, maxX, maxY } = bounds;
  const drawn = (maxX - minX + 1) * (maxY - minY + 1);

  // Adaylar sırayla deneniyor ve İÇERİK EŞİĞİ seçici olarak kullanılıyor: bir aday görüntünün
  // neredeyse tamamını siliyorsa o zemin değil logonun kendisidir, sıradakine geçiliyor. Eşik
  // eskiden veto idi — yanlış aday bulununca işlem tamamen iptal oluyordu.
  //
  // Tarama sınır kutusuyla sınırlı: dışarısı zaten alfa 0 ve kural onu 0'da bırakıyor.
  for (const [r, g, b] of backgroundCandidates(pixels, width, bounds)) {
    let kept = 0;

    for (let y = minY; y <= maxY; y += 1) {
      for (
        let x = minX, i = (y * width + minX) * 4;
        x <= maxX;
        x += 1, i += 4
      ) {
        if (alphaFor(pixels, i, r, g, b) > 0) {
          kept += 1;
        }
      }
    }

    if (kept / drawn < MIN_CONTENT_RATIO) {
      continue;
    }

    for (let y = minY; y <= maxY; y += 1) {
      for (
        let x = minX, i = (y * width + minX) * 4;
        x <= maxX;
        x += 1, i += 4
      ) {
        pixels[i + 3] = alphaFor(pixels, i, r, g, b);
      }
    }

    ctx.putImageData(image, 0, 0);

    return;
  }
};

// `padding` çıktı kenarının kaçta kaçının boş bırakılacağı (0–0.4): menü yükleme görselini 12px
// yuvarlatılmış 80px'lik bir kutuya basıyor, kenara dayanan içerik köşelerden kırpılıyor.
// `removeBackground` köşelerden örneklenen düz zemini siler (bkz. clearBackground).
//
// `type` çıktı biçimi ve ŞEFFAFLIĞI birlikte belirliyor, ayrı bir bayrak yok:
//
//   image/png  — marka görselleri. Zemin şeffaf kalır, kırpılan görsel nereye basılıyorsa oranın
//                zeminini görür. Logo/amblem için tek doğru seçenek.
//   image/jpeg — ürün/kategori/şube FOTOĞRAFLARI. Aynı kadrajın PNG'si birkaç MB, JPEG'i birkaç
//                yüz KB; görseller storage.noxussoft.com üzerinden Worker kotasına sayıldığı için
//                fark doğrudan faturaya yazıyor.
//
// JPEG alfa taşımıyor ve şeffaf pikselleri SİYAH kodluyor — o yüzden çizimden önce zemin beyaza
// boyanıyor ve zemin temizliği hiç çalıştırılmıyor. İkisi de sessizce simsiyah bir kutu üretirdi.
// Görselin tamamını çerçeveye sığdırır; artan yeri aynı görselin bulanık, çerçeveyi dolduran
// kopyası kapatır.
//
// Bulanık zemin 1.08 kat büyük çiziliyor: blur kenarlarda saydamlığa doğru sönüyor ve tam ölçüde
// çizilirse çerçevenin kenarında soluk bir çerçeve izi kalıyor.
const BACKDROP_BLUR = 28;
const BACKDROP_OVERSCAN = 1.08;

const drawContain = (ctx, img, width, height, inset) => {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;

  const cover = Math.max(width / iw, height / ih) * BACKDROP_OVERSCAN;
  ctx.filter = `blur(${BACKDROP_BLUR}px)`;
  ctx.drawImage(
    img,
    (width - iw * cover) / 2,
    (height - ih * cover) / 2,
    iw * cover,
    ih * cover,
  );
  ctx.filter = "none";

  const box = { width: width - inset * 2, height: height - inset * 2 };
  const fit = Math.min(box.width / iw, box.height / ih);
  const w = iw * fit;
  const h = ih * fit;

  ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
};

// `fit` — çıktının çerçeveyle ilişkisi. Üçü de bir şeyi feda ediyor, "doğrusu" yok:
//
//   'crop'    Kadrajdaki alan çerçeveyi DOLDURUR, dışarısı atılır. Neyin gittiğini kullanıcı seçer.
//   'contain' Görselin TAMAMI çerçeveye sığar, artan yeri bulanık kopyası doldurur. Hiçbir şey
//             gitmez ama çıktının bir kısmı bulanık zemindir.
//   'none'    Kırpma YOK: görsel kendi oranıyla çıkar, yalnızca ölçüsü küçültülür. Hiçbir şey
//             gitmez ve bulanık zemin de olmaz — ama menüdeki karo `background-size: cover`
//             bastığı için kırpmayı TARAYICI yapar, ortadan, kullanıcının seçemediği bir yerden.
//
// Menü karoları sabit oranlı: 3.5:1 bir fotoğraf 1.8:1 bir karoda ne yaparsan yap tam görünmez.
// Seçenekler bu gerçeği ortadan kaldırmıyor, kırpmanın NEREDE ve KİM tarafından yapılacağını
// belirliyor.
//
// 'contain'de boşluk düz renk değil bulanık kopya: düz beyaz/siyah bant fotoğrafı pul gibi
// gösteriyor, bulanık zemin ise karonun üstündeki başlık yazısıyla da uyumlu duruyor.
export const cropImage = async (
  file,
  area,
  {
    width,
    height,
    padding = 0,
    removeBackground = false,
    type = "image/png",
    quality,
    fit = "crop",
  } = {},
) => {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  const contain = fit === "contain";
  const untouched = fit === "none";

  if (untouched) {
    // Kendi oranı korunuyor; yalnızca en uzun kenar hedefin en uzun kenarına indiriliyor.
    // Sınır dosya boyutu için: telefon fotoğrafı 4000px geliyor ve menüde en geniş karo 560px.
    const longest =
      Math.max(width ?? 0, height ?? 0) ||
      Math.max(img.naturalWidth, img.naturalHeight);
    const shrink = Math.min(
      1,
      longest / Math.max(img.naturalWidth, img.naturalHeight),
    );

    canvas.width = Math.max(1, Math.round(img.naturalWidth * shrink));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * shrink));
  } else {
    // 'contain'de çerçevenin TAMAMI çıktı: hedef ölçü doğrudan kullanılıyor, ama kaynak daha
    // küçükse büyütülmüyor (kadraj yolundaki kural ile aynı).
    const source = contain
      ? { width: img.naturalWidth, height: img.naturalHeight }
      : area;
    const scale = Math.min(
      1,
      width ? width / source.width : 1,
      height ? height / source.height : 1,
    );

    canvas.width = Math.max(
      1,
      Math.round((contain ? width : area.width) * (contain ? 1 : scale)),
    );
    canvas.height = Math.max(
      1,
      Math.round((contain ? height : area.height) * (contain ? 1 : scale)),
    );
  }

  const ctx = canvas.getContext("2d");
  const transparent = type === "image/png";

  if (!transparent) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // Boşluk çıktıyı küçültmüyor, içeriği içeri alıyor: dosya ölçüsü sabit kalsın ki menü
  // tarafındaki sabit kutu hep aynı davransın.
  const inset = Math.round(
    Math.min(canvas.width, canvas.height) * Math.min(0.4, Math.max(0, padding)),
  );

  if (untouched) {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  } else if (contain) {
    drawContain(ctx, img, canvas.width, canvas.height, inset);
  } else {
    ctx.drawImage(
      img,
      area.x,
      area.y,
      area.width,
      area.height,
      inset,
      inset,
      canvas.width - inset * 2,
      canvas.height - inset * 2,
    );
  }

  if (removeBackground && transparent) {
    clearBackground(ctx, canvas.width, canvas.height);
  }

  const extension = transparent ? "png" : "jpg";

  return canvasToFile(
    canvas,
    `crop-${canvas.width}x${canvas.height}.${extension}`,
    type,
    quality,
  );
};

// Resize an uploaded image (incl. SVG) to a square PNG File via <canvas>, used to derive the
// favicon / apple-touch-icon from the tenant's SQUARE MARK (the logo fallback was removed).
// `bg` fills the backdrop (apple-touch icons should be opaque); leave null for a transparent
// favicon.
export const resizeToPng = async (file, size, bg = null) => {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  if (bg) {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);
  }

  const sourceW = img.naturalWidth || size;
  const sourceH = img.naturalHeight || size;
  const scale = Math.min(size / sourceW, size / sourceH);
  const w = sourceW * scale;
  const h = sourceH * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);

  return canvasToFile(canvas, `icon-${size}.png`, "image/png");
};

// File için object URL. TEK desteklenen çağıran hooks/useObjectUrl — bırakma sorumluluğu orada.
// Doğrudan çağırma: revoke edecek kimse olmaz.
export const previewUrl = async (file) =>
  URL.createObjectURL(await withSvgSize(file));
